/**
 * HEDEF: DROPBOX — "Uygulama klasörü" (Apps/<uygulama adı>).
 *
 * Yetki: OAuth 2 + PKCE, `token_access_type=offline`. Dropbox genel (sırsız)
 * istemcilere de UZUN ÖMÜRLÜ yenileme anahtarı veriyor; yani kurulumdan sonra
 * yedek kullanıcı etkileşimi olmadan sürer. Dört bulut seçeneğinin içinde
 * gerçekten "kur ve unut" olan tek OAuth hedefi bu.
 *
 * Kapsam: uygulama kaydı "App folder" erişimiyle açılır; Parafon yalnızca o
 * klasörü görür, kullanıcının geri kalan Dropbox'ına erişemez.
 */

import { M } from "../../dil";
import type { BulutDosyasi, BulutHedefi, ErisimAnahtari, HedefKaydi } from "../tipler";
import { BaglantiGerekliHatasi } from "../yedekleyici";
import {
  BelirtecHatasi,
  bitisHesapla,
  donusAdresi,
  formGonder,
  gecerliMi,
  pkceUret,
  rastgele,
} from "../oauth";
import type { BulutBagimliliklari, Pkce } from "../oauth";

const YETKI = "https://www.dropbox.com/oauth2/authorize";
const BELIRTEC = "https://api.dropboxapi.com/oauth2/token";
const API = "https://api.dropboxapi.com/2";
const ICERIK = "https://content.dropboxapi.com/2";
const KAPSAM = "files.content.write files.content.read files.metadata.read";

type DropboxKaydi = Extract<HedefKaydi, { tur: "dropbox" }>;

/** Kullanıcı hareketinin içinden: açılır pencereyle bağlanır. */
export async function dropboxBaglan(istemci: string, d: BulutBagimliliklari): Promise<DropboxKaydi> {
  const state = rastgele();
  const tut: { pkce: Pkce | null } = { pkce: null };
  const params = await d.yetkilendir(async () => {
    const pkce = await pkceUret();
    tut.pkce = pkce;
    const q = new URLSearchParams({
      client_id: istemci,
      response_type: "code",
      code_challenge: pkce.meydanOkuma,
      code_challenge_method: "S256",
      token_access_type: "offline",
      redirect_uri: donusAdresi(),
      scope: KAPSAM,
      state,
    });
    return `${YETKI}?${q.toString()}`;
  }, state);
  const kod = params.get("code");
  if (kod === null || tut.pkce === null) throw new Error(M.yedek.hedef.dropboxKodYok);
  const g = await formGonder(d.fetch, BELIRTEC, {
    code: kod,
    grant_type: "authorization_code",
    code_verifier: tut.pkce.dogrulayici,
    client_id: istemci,
    redirect_uri: donusAdresi(),
  });
  const yenileme = g["refresh_token"];
  const erisim = g["access_token"];
  if (typeof yenileme !== "string" || typeof erisim !== "string") {
    throw new Error(M.yedek.hedef.dropboxYenilemeYok);
  }
  return {
    tur: "dropbox",
    yenilemeAnahtari: yenileme,
    erisim: { deger: erisim, bitis: bitisHesapla(d.saat(), g["expires_in"]) },
  };
}

/** Dropbox-API-Arg başlığı HTTP-güvenli olmalı: ASCII dışını \uXXXX kaçır. */
function apiArg(v: unknown): string {
  return JSON.stringify(v).replace(/[\u007f-￿]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, "0")}`);
}

export function dropboxHedefi(ilk: DropboxKaydi, istemci: string, d: BulutBagimliliklari): BulutHedefi {
  let kayit: DropboxKaydi = ilk;

  async function yenile(): Promise<string> {
    try {
      const g = await formGonder(d.fetch, BELIRTEC, {
        grant_type: "refresh_token",
        refresh_token: kayit.yenilemeAnahtari,
        client_id: istemci,
      });
      const erisim: ErisimAnahtari = {
        deger: String(g["access_token"]),
        bitis: bitisHesapla(d.saat(), g["expires_in"]),
      };
      kayit = { ...kayit, erisim };
      return erisim.deger;
    } catch (e: unknown) {
      if (e instanceof BelirtecHatasi && (e.kod === "invalid_grant" || e.durum === 400 || e.durum === 401)) {
        throw new BaglantiGerekliHatasi(M.yedek.hedef.dropboxGeriAlindi);
      }
      throw e;
    }
  }

  async function anahtar(): Promise<string> {
    if (kayit.erisim !== null && gecerliMi(kayit.erisim.bitis, d.saat())) return kayit.erisim.deger;
    return yenile();
  }

  /** 401'de bir kez yeniler ve yeniden dener. */
  async function cagir(url: string, init: RequestInit): Promise<Response> {
    for (let deneme = 0; deneme < 2; deneme += 1) {
      const a = deneme === 0 ? await anahtar() : await yenile();
      const y = await d.fetch(url, {
        ...init,
        headers: { ...(init.headers as Record<string, string>), Authorization: `Bearer ${a}` },
      });
      if (y.status === 401 && deneme === 0) continue;
      if (!y.ok) {
        const metin = await y.text().catch(() => "");
        if (y.status === 401) throw new BaglantiGerekliHatasi(M.yedek.hedef.dropboxReddetti);
        throw new Error(`Dropbox ${y.status}: ${metin.slice(0, 200)}`);
      }
      return y;
    }
    throw new BaglantiGerekliHatasi(M.yedek.hedef.dropboxReddetti);
  }

  const jsonCagir = (yol: string, govde: unknown): Promise<Response> =>
    cagir(`${API}${yol}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(govde),
    });

  return {
    tur: "dropbox",
    konum: M.yedek.hedef.dropboxKonum,
    hazirlik: () => Promise.resolve("hazir"),
    async yenidenBaglan() {
      kayit = await dropboxBaglan(istemci, d);
    },
    async yaz(ad, metin) {
      await cagir(`${ICERIK}/files/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "Dropbox-API-Arg": apiArg({ path: `/${ad}`, mode: "overwrite", mute: true }),
        },
        body: metin,
      });
    },
    async listele() {
      const out: BulutDosyasi[] = [];
      let y = (await (await jsonCagir("/files/list_folder", { path: "" })).json()) as ListeYaniti;
      for (;;) {
        for (const e of y.entries) {
          if (e[".tag"] === "file" && e.name.endsWith(".pfai")) {
            out.push({ ad: e.name, degisti: e.server_modified ?? null, boyut: e.size ?? null });
          }
        }
        if (!y.has_more) break;
        y = (await (await jsonCagir("/files/list_folder/continue", { cursor: y.cursor })).json()) as ListeYaniti;
      }
      return out;
    },
    async oku(ad) {
      const y = await cagir(`${ICERIK}/files/download`, {
        method: "POST",
        headers: { "Dropbox-API-Arg": apiArg({ path: `/${ad}` }) },
      });
      return y.text();
    },
    async sil(ad) {
      await jsonCagir("/files/delete_v2", { path: `/${ad}` });
    },
    kayit: () => kayit,
  };
}

interface ListeYaniti {
  readonly entries: readonly {
    readonly ".tag": string;
    readonly name: string;
    readonly server_modified?: string;
    readonly size?: number;
  }[];
  readonly cursor: string;
  readonly has_more: boolean;
}
